/* eslint-disable max-lines */
/* eslint-disable react/jsx-handler-names */
"use client";

import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import {
  getAvailablePincodes,
  getPlaceDetailsByPlaceId,
  getPincodeSetter,
  getPincodes,
} from "../../../api/pincodeLocationSuggestion";
import { GOOGLE_KEYS, googleApiUrl } from "../../../constants/keys/locationLock";
import { charactersAndDigitsOnlyRegex, digitsOnlyRegex } from "../../../constants/regex";
import {
  FROM_LOCATION_LOCK,
  INTEGRATED_PINCODE_GOOGLE_SUGGESTION,
  LOCATION_MANDATORY,
  NUDGE_TEXT_FOR_AREA_SECTOR,
} from "../../../constants/common/locationFieldConstants";
import { DELIVERY_SHOULD_BE_SCHEDULED_MESSAGE } from "../../../constants/pageConstants/pdpConstants";
import {
  clearMapFormLocation,
  onFocusRequestFromTextfield,
  setIsHideSavedAddresses,
  setIsSavedAddressUserName,
  setIsShowCartEmpty,
  setIsShowDesktopLocationLockAction,
  setIsShowMobileLocationLockAction,
  setIsShowMobileLocationLockDrawerAction,
  setMandateErrorAction,
  setSelectedDomesticLocation,
  setSelectedDomesticLocationForCart,
  setUnserviceableCartItems,
  setIsClearCartDueToCountryChange,
  setSkipCartValidationOnce,
} from "../../../store/slices/locationLock";
import { debounce, formatClassNames, pageName, removeHashtagFromURL } from "../../../utils/common";
import CustomImage from "../../atoms/customImage";
import TextField from "../../atoms/textField";
import {
  LAST_VISITED_ADDRESS,
  LAST_VISITED_PINCODE,
  LAST_VISITED_PLACE_ID,
  LAST_VISITED_LAT_LONG,
  LAST_VISITED_ADDRESS_LAT_LONG,
  LAST_VISITED_ACCURACY_TYPE,
  LAST_VISITED_ACCURACY_LEVEL,
  LAST_VISITED_PLACE_TYPES,
  SELECTED_SAVED_ADDRESS,
  SELECTED_SAVED_ADDRESS_USERNAME,
} from "../../../constants/storage";
import { getFilteredCitiesList, getGateAutoTriggerFlag } from "../../../utils/locationLock";
import { getCleverTapPageType } from "../../../utils/fnpPageType";
import { isHomePathname } from "../../../utils/dynamicHomeUtils/storeUrl";
import {
  hasCartItemsFromDifferentCatalog,
  resolveClearCartModeForLocation,
  validateCartServiceabilityOnLocationChange,
  consumeSkipCartValidationOnce,
} from "../../../utils/locationLock/validateCartServiceabilityOnLocationChange";
import { setCartItems } from "../../../store/slices/cartSlice";
import ListDropDownSection from "./ListDropDownSection";
import { CONFIRM, COUNTRIES } from "../../../constants/common/common";
import CookieUtil from "../../../utils/storage/cookieUtil";
import useAnalytics from "../../../analytics/useAnalytics";
import { cleverTapCommonEvents, validationFor } from "../../../analytics/clevertapConstants";
import {
  resetMultiProductDateRange,
  setDeliveryDetailsAction,
  successToFetchBlockedDatesAction,
  requestResetShippingDateAndSlot,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import { unlockBody } from "../../../utils/document";
import ButtonComponent from "../../atoms/button";
import Portal from "../Portal";
import isEmpty from "lodash.isempty";
import { usePathname } from "next/navigation";
import {
  setDeliveryDateErrorFromAddToCart,
  setPincodeErrorFromAddToCart,
  setSelectedDateFromAddToCart,
} from "../../../store/slices/pdpSlice";
import { GA4_EVENTS } from "../../../analytics/gaConstant";
import { requestDesktopFeed } from "../../../store/slices/dynamicHomeSlice";
import { execDataLayer } from "../../../utils/cache-layer/cacheLayer";
import { endpointConst } from "../../../utils/cache-layer/cacheLayerConstants";
import { fetchAccuracyWeightage } from "../../../api/common";
import { getAccuracyLevelForTypes, isLowAccuracyPlaceTypes } from "../../../utils/checkout/deliveryAddress";
import Typography from "../../atoms/typography";
import { resetPdpAddOnState } from "../../../store/slices/pdpAddonSlice";
import BlankSpaceUI from "../blankSpaceUI";
import CountryChangeInfoMobile from "../countryChangeInfoMobile";
import { constants } from "../../../constants/pageConstants/pdpConstants";
import { InfoIcon, XCircleIcon } from "@phosphor-icons/react";

/**
 * Lat/long of a Google place from the Places library (fallback when the place details API has none).
 * @param {string} placeId
 * @returns {Promise<{lat: number, long: number}|null>}
 */
const getGooglePlaceLatLong = (placeId) =>
  new Promise((resolve) => {
    const places = typeof window !== "undefined" ? window.google?.maps?.places : null;
    if (!placeId || !places?.PlacesService) {
      resolve(null);
      return;
    }
    new places.PlacesService(document.createElement("div")).getDetails(
      { placeId, fields: ["geometry"] },
      (place, status) => {
        const location = status === places.PlacesServiceStatus.OK ? place?.geometry?.location : null;
        resolve(location ? { lat: location.lat(), long: location.lng() } : null);
      },
    );
  });

/**
 * PincodeLocationSuggestion component provides suggestions for pincode or location input
 * based on user input, using Google Places API and domestic pincode list.
 * @component
 */
const PincodeLocationSuggestion = ({
  selectedCountry,
  isSuggestionSelected,
  setSuggestionSelected,
  searchValue,
  setSearchValue,
  onHandleContinueShopping,
  isFromPDP = false,
  isFromCheckout = false,
  isError,
  messageText,
  messageClass,
  isDisable,
  onPincodeLocationClickHandler,
  urlIdentifier,
  cleverTapPDPCommonData,
  setMandatoryError,
  isLocationNudgeVisible,
  setIsLocationNudgeVisible,
  isSeparateFieldsDesign = false,
  isContinueButtonHidden = false,
  onClearSavedAddressSelection,
  serviceabilityErrorMessage,
  isCountryChanged = false,
  isProductUnavailable = false,
  onExploreAvailableProducts,
  onSuggestionsVisibilityChange,
  onFocusChangeHandler,
  isFromOutsideDrawer = false,
  isFromGiftFinderGate = false,
  locationCaptureMode,
  onCaptureLocation,
}) => {
  const isJoinedFieldLayout = isFromPDP && !isSeparateFieldsDesign;
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const pathname = usePathname();
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : null;
  const {
    isMobile,
    cityListData,
    domesticPincodeList,
    configData,
    selectedDomesticLocation,
    isShowMobileLocationLock,
    isShowMobileLocationLockDrawer,
    savedAddresses,
    onFocusChangeFromTextfield,
    mandateErrorClick,
    deliveryDateError,
    cartCount,
    loggedin,
    catalogId,
    skipCartValidationOnce,
    shouldClearLocationLockInput,
    isDeliveryDatesLoading,
    deliveryDates,
    isDatesAvailable,
  } = useSelector(
    (state) => ({
      isMobile: state?.appConfig?.isMobile,
      cityListData: state.locationLock.cityListData,
      domesticPincodeList: state?.pincodeLocationSuggestion?.pincodeList,
      configData: state?.locationLock?.configData,
      selectedDomesticLocation: state?.locationLock.selectedDomesticLocation,
      isShowMobileLocationLock: state?.locationLock?.isShowMobileLocationLock,
      isShowMobileLocationLockDrawer: state?.locationLock?.isShowMobileLocationLockDrawer,
      savedAddresses: state?.savedAddress?.savedAddresses || [],
      onFocusChangeFromTextfield: state.locationLock.onFocusChangeFromTextfield,
      mandateErrorClick: state?.locationLock?.mandateErrorClick,
      deliveryDateError: state?.pdp?.deliveryDateError,
      cartCount: state?.cart?.cartCount,
      loggedin: state?.fus?.loginDetail?.loggedin,
      catalogId: state?.appConfig?.catalogId,
      skipCartValidationOnce: state?.locationLock?.skipCartValidationOnce,
      shouldClearLocationLockInput: state?.locationLock?.shouldClearLocationLockInput,
      isDeliveryDatesLoading: state?.shippingDateAndSlotSelection?.isDeliveryDatesLoading,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      isDatesAvailable: state?.shippingDateAndSlotSelection?.isDatesAvailable,
    }),
    shallowEqual,
  );

  const {
    isAutoGateMandateOnDesktopHome,
    isAutoGateMandateOnDesktopPLP,
    isAutoGateMandateOnMobileHome,
    isAutoGateMandateOnMobilePLP,
    isAutoGateEnabledOnDesktopHome,
    isAutoGateEnabledOnMobileHome,
    isAutoGateEnabledOnDesktopPLP,
    isAutoGateEnabledOnMobilePLP,
    mandateGatePLPList,
  } = configData;
  const [selectedRow, setSelectedRow] = useState(0);
  const [timeoutId, setTimeoutId] = useState(null);
  const [availablePincodes, setAvailablePincodes] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [deliveryType, setDeliveryType] = useState("");
  const [eventTriggered, setEventTriggered] = useState(false);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState({});
  const [isMandatoryError, setIsMandatoryError] = useState(false);
  const [statusText, setStatusText] = useState("");
  const [isErrorMsgAnimate, setIsErrorMsgAnimate] = useState(false);
  const [isCartScheduleMessageVisible, setIsCartScheduleMessageVisible] = useState(false);
  const skipCartValidationOnceRef = useRef(skipCartValidationOnce);
  skipCartValidationOnceRef.current = skipCartValidationOnce;
  // PDP: defer cart-items/serviceable APIs until delivery-dates confirms the product is deliverable.
  const pendingCartValidationRef = useRef(null);
  const onValidateCartServiceabilityForLocationRef = useRef(null);

  useEffect(() => {
    onSuggestionsVisibilityChange?.(suggestions?.length > 0);
  }, [suggestions, onSuggestionsVisibilityChange]);

  const isHome = isHomePathname(pathname);
  const isPLP = pathname?.includes("-lp");
  const isCheckout = isFromCheckout || pathname?.includes("/checkout");
  const isAutoForDesktop = (isAutoGateEnabledOnDesktopHome && isHome) || (isAutoGateEnabledOnDesktopPLP && isPLP);
  const isAutoForMobile = (isAutoGateEnabledOnMobileHome && isHome) || (isAutoGateEnabledOnMobilePLP && isPLP);
  const isAutoTriggerLock = getGateAutoTriggerFlag(mandateGatePLPList);
  // The gift finder gate treats location as optional — the user may confirm a country
  // alone, or skip the question outright — so the mandate flags never apply there.
  const isMandateForDesktop =
    !isFromGiftFinderGate &&
    (isCheckout
      ? true
      : !isAutoTriggerLock && ((isAutoGateMandateOnDesktopHome && isHome) || (isAutoGateMandateOnDesktopPLP && isPLP)));
  const isMandateForMobile =
    !isFromGiftFinderGate &&
    (isCheckout
      ? true
      : !isAutoTriggerLock && ((isAutoGateMandateOnMobileHome && isHome) || (isAutoGateMandateOnMobilePLP && isPLP)));

  const lastVisitedPincode = CookieUtil.getCookie(LAST_VISITED_PINCODE);
  const lastVisitedAddress = CookieUtil.getCookie(LAST_VISITED_ADDRESS);

  const checkLoginStatus = useSelector((state) => state.fus.loginDetail?.loggedin);

  const isVisibleLocationMobileDrawer = isShowMobileLocationLockDrawer && !isShowMobileLocationLock;

  const isAutoFocus = isVisibleLocationMobileDrawer
    ? false
    : isEmpty(savedAddresses)
      ? onFocusChangeFromTextfield
      : false;

  const commonPageData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    mandatory: isAutoTriggerLock ? false : isMobile ? isMandateForMobile : isMandateForDesktop,
    auto: isAutoTriggerLock ? false : isMobile ? isAutoForMobile : isAutoForDesktop,
    user_initiated: isAutoTriggerLock
      ? true
      : isMobile
        ? !(isAutoForMobile || isMandateForMobile)
        : !(isAutoForDesktop || isMandateForDesktop),
    clean_url: pathname,
    complete_url: completePathName,
  };
  const ishomePlpPdpLocationData = isHome || isPLP ? commonPageData : cleverTapPDPCommonData;
  /**
   * Builds CleverTap payload for location pincode/area select events.
   * @param {Object} overrides - Event-specific fields (pincode, area, etc.)
   * @returns {Object}
   */
  const buildLocationSelectCleverTapData = (overrides = {}) => {
    const { pageType, ...restLocationData } = ishomePlpPdpLocationData || {};

    return {
      ...restLocationData,
      page_type: getCleverTapPageType(pageType || restLocationData?.page_type),
      city: restLocationData?.city || "",
      mandatory: isAutoTriggerLock ? false : isMobile ? isMandateForMobile : isMandateForDesktop,
      auto: isAutoTriggerLock ? false : isMobile ? isAutoForMobile : isAutoForDesktop,
      user_initiated: isAutoTriggerLock
        ? true
        : isMobile
          ? !(isAutoForMobile || isMandateForMobile)
          : !(isAutoForDesktop || isMandateForDesktop),
      is_saved_address_available: Boolean(savedAddresses?.length),
      is_bottom_sheet: Boolean(isMobile && (isShowMobileLocationLock || isShowMobileLocationLockDrawer)),
      flow: "new",
      ...overrides,
    };
  };

  /**
   * The location details Confirm should commit.
   *
   * Picking a suggestion is the normal path and fills `selectedLocationDetails`. Every
   * caller also has to honour a prefill the user never touched: LocationLock restores it
   * into `searchValue` from redux/cookies without going through a selection handler, so
   * local state stays empty and Confirm would otherwise read as "clear my location".
   * Reading redux/cookies directly covers that, including the cases where only a pincode
   * or only an address was stored.
   *
   * Clearing the field flips `isSuggestionSelected` off, so an intentional clear still
   * yields `{}` and the reset path in LocationLock runs.
   *
   * @returns {{pincode: string, location: string}|{}}
   */
  const getLocationDetailsForContinue = () => {
    if (!isEmpty(selectedLocationDetails)) return selectedLocationDetails;
    if (!isSuggestionSelected) return {};

    const pincode = selectedDomesticLocation?.pincode || lastVisitedPincode;
    const address = selectedDomesticLocation?.address || lastVisitedAddress;
    return pincode ? { pincode, location: address || "" } : {};
  };

  useEffect(() => {
    if (mandateErrorClick) {
      setIsErrorMsgAnimate(true);
      setTimeout(() => {
        setIsErrorMsgAnimate(false);
        dispatch(setMandateErrorAction(false));
      }, 500);
    }
  }, [mandateErrorClick, dispatch]);

  useEffect(() => {
    if (!shouldClearLocationLockInput) {
      return;
    }
    setSelectedLocationDetails({});
    setSuggestionSelected(false);
    setSelectedRow(0);
    setIsCartScheduleMessageVisible(false);
    pendingCartValidationRef.current = null;
    setIsLocationNudgeVisible?.(false);
    if (isContinueButtonHidden && typeof onClearSavedAddressSelection === "function") {
      onClearSavedAddressSelection();
    }

    dispatch(setIsSavedAddressUserName(null));
    dispatch(setDeliveryDetailsAction({}));
    dispatch(resetMultiProductDateRange());
    dispatch(successToFetchBlockedDatesAction({}));
    dispatch(requestResetShippingDateAndSlot());
    dispatch(resetPdpAddOnState());
    dispatch(setSelectedDomesticLocation({}));
    dispatch(setSelectedDateFromAddToCart(""));
    dispatch(setPincodeErrorFromAddToCart(false));
    if (deliveryDateError) {
      dispatch(setDeliveryDateErrorFromAddToCart(false));
    }

    const pathOptions = { path: "/" };
    CookieUtil.deleteCookie(SELECTED_SAVED_ADDRESS, pathOptions);
    CookieUtil.deleteCookie(SELECTED_SAVED_ADDRESS_USERNAME, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_PINCODE, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_ADDRESS, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_LAT_LONG, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_PLACE_TYPES, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_LEVEL, pathOptions);
    CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_TYPE, pathOptions);
    // Parent clears the flag after searchValue reset; do not depend on unstable callbacks.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldClearLocationLockInput, dispatch, deliveryDateError]);

  // Hide pincode schedule nudge when a saved address is selected (Confirm mode).
  useEffect(() => {
    if (isContinueButtonHidden) {
      setIsCartScheduleMessageVisible(false);
    }
  }, [isContinueButtonHidden]);

  // Cross-country: never show schedule nudge; cancel any deferred OOS validation.
  useEffect(() => {
    if (!isCountryChanged) {
      return;
    }
    setIsCartScheduleMessageVisible(false);
    pendingCartValidationRef.current = null;
  }, [isCountryChanged]);

  useEffect(() => {
    getAvailablePincodes().then((res) => {
      setAvailablePincodes(res);
    });

    // In order to load Google script
    const existingScript = document.getElementById("googleMaps");
    const currentKey = GOOGLE_KEYS[Math.floor(Math.random() * GOOGLE_KEYS.length)];
    if (!existingScript) {
      const script = document.createElement("script");
      script.src = `${googleApiUrl}?key=${currentKey}&libraries=places`;
      script.id = "googleMaps";
      document.body.appendChild(script);
    }
  }, []);

  /**
   * Google API place prediction service to provide address suggestions
   * @param {string} value - The input value for which predictions are to be fetched.
   */
  const googlePlacePredictionCall = useCallback(async (value) => {
    const autocompleteService = new window.google.maps.places.AutocompleteService();
    const request = { input: value, componentRestrictions: { country: "in" }, types: ["geocode", "establishment"] };
    await autocompleteService?.getPlacePredictions(request, (predictions) => {
      setDeliveryType("location");

      setSuggestions(predictions);
    });
  }, []);

  // Memoize the debounce call with useMemo
  const debouncedPredictionCall = useMemo(() => {
    return debounce(googlePlacePredictionCall, 600);
  }, [googlePlacePredictionCall]);

  /**
   * Clears saved address selection when user manually enters pincode or city.
   */
  const clearSavedAddressSelection = () => {
    if (isContinueButtonHidden && typeof onClearSavedAddressSelection === "function") {
      onClearSavedAddressSelection();
    }
  };

  /**
   * Commits a manually picked pincode/location to Redux right away, so callers
   * relying on `selectedDomesticLocation` (e.g. an external Continue button) unlock
   * immediately. Unlike `onContinueClick`, this skips the close/redirect side effects,
   * which stay gated behind an explicit confirm action.
   * @param {object} locationDetails - The selected pincode/location pair.
   */
  const commitLocationSelection = (locationDetails) => {
    if (isEmpty(locationDetails) || isFromCheckout) return;
    clearSavedAddressSelection();
    dispatch(setSelectedDomesticLocation({ pincode: locationDetails?.pincode, address: locationDetails?.location }));
    trackEvent(
      GA4_EVENTS.DELIVERY_LOCATION_ADDED,
      {
        eventCategory: "delivery_location_interaction",
        eventAction: "delivery_location_added",
        eventLabel: "continue_click",
        interaction_type: "manual",
        page_type: window?.fnpPageType,
      },
      ["ga4"],
    );
  };

  /**
   * Validates cart serviceability for a newly selected pincode.
   * Runs the serviceability API on selection. ClearCart opens only when
   * `openClearCart` is true (Confirm click).
   *
   * @param {string|number} selectedPincode - Newly selected pincode.
   * @param {string} location - Selected location/area label.
   * @param {Object} [options]
   * @param {boolean} [options.openClearCart=false] - When true, open ClearCart if unserviceable.
   * @param {boolean} [options.isCurrentProductServiceable=true] - Skip cart APIs when PDP product is not deliverable.
   * @returns {Promise<boolean>} True when location update should continue.
   */
  const onValidateCartServiceabilityForLocation = useCallback(
    async (selectedPincode, location, { openClearCart = false, isCurrentProductServiceable = true } = {}) => {
      const shouldSkipCartValidation = Boolean(skipCartValidationOnceRef.current) || consumeSkipCartValidationOnce();
      if (shouldSkipCartValidation) {
        // Clear both Redux and any leftover sync arm so a later selection is not skipped.
        skipCartValidationOnceRef.current = false;
        dispatch(setSkipCartValidationOnce(false));
        setIsCartScheduleMessageVisible(false);
        return true;
      }

      // Cross-country switch: skip same-catalog OOS / schedule nudge. Continue → onHandleContinueShopping
      // opens country ClearCart (Change Country → clear + redirect) once.
      if (isCountryChanged) {
        setIsCartScheduleMessageVisible(false);
        return true;
      }

      // Schedule nudge only when we are about to hit cart serviceability APIs.
      const willTriggerServiceabilityApi =
        Boolean(selectedPincode) && cartCount > 0 && !isFromCheckout && isCurrentProductServiceable;
      if (willTriggerServiceabilityApi) {
        setIsCartScheduleMessageVisible(true);
      } else {
        setIsCartScheduleMessageVisible(false);
      }

      const validationResult = await validateCartServiceabilityOnLocationChange({
        selectedPincode,
        cartItemCount: cartCount,
        catalogId,
        savedAddresses,
        isFromCheckout,
        isCurrentProductServiceable,
      });

      if (Array.isArray(validationResult?.orderList) && validationResult.orderList.length > 0) {
        dispatch(setCartItems(validationResult.orderList));
      }

      if (!validationResult?.shouldProceed) {
        const { isCountryChange, unserviceableItems } = resolveClearCartModeForLocation({
          orderList: validationResult?.orderList,
          catalogId,
          serviceabilityItems: validationResult?.serviceabilityResult?.items,
        });
        dispatch(setIsClearCartDueToCountryChange(isCountryChange));
        dispatch(setUnserviceableCartItems(unserviceableItems));
        dispatch(
          setSelectedDomesticLocationForCart({
            pincode: selectedPincode,
            address: location,
          }),
        );
        if (openClearCart) {
          dispatch(setIsShowCartEmpty(true));
        }
        return false;
      }

      // Cross-catalog with serviceable response still requires country ClearCart on Confirm.
      if (openClearCart && hasCartItemsFromDifferentCatalog(validationResult?.orderList, catalogId)) {
        dispatch(setIsClearCartDueToCountryChange(true));
        dispatch(setUnserviceableCartItems([]));
        dispatch(
          setSelectedDomesticLocationForCart({
            pincode: selectedPincode,
            address: location,
          }),
        );
        dispatch(setIsShowCartEmpty(true));
        return false;
      }

      dispatch(setUnserviceableCartItems([]));
      return true;
    },
    [cartCount, catalogId, savedAddresses, isFromCheckout, isCountryChanged, dispatch],
  );
  onValidateCartServiceabilityForLocationRef.current = onValidateCartServiceabilityForLocation;

  /**
   * On PDP, wait for delivery-dates to finish for the newly selected pincode before
   * calling cart-items / products/serviceable and showing the schedule message.
   */
  useEffect(() => {
    const pending = pendingCartValidationRef.current;
    if (!pending || !isFromPDP || isFromCheckout) {
      return;
    }

    if (pending.phase === "awaiting_start") {
      if (isDeliveryDatesLoading) {
        pending.phase = "awaiting_result";
      }
      return;
    }

    if (pending.phase !== "awaiting_result" || isDeliveryDatesLoading) {
      return;
    }

    if (String(selectedDomesticLocation?.pincode || "") !== String(pending.pincode || "")) {
      return;
    }

    pendingCartValidationRef.current = null;
    const datesList = Array.isArray(deliveryDates?.deliveryDates)
      ? deliveryDates.deliveryDates
      : Array.isArray(deliveryDates)
        ? deliveryDates
        : [];
    // Match PDP NSA check: explicit isDatesAvailable, with dates list as fallback.
    const isDeliverable =
      deliveryDates?.isDatesAvailable === true ||
      isDatesAvailable === true ||
      (deliveryDates?.isDatesAvailable !== false && isDatesAvailable !== false && datesList.length > 0);

    if (!isDeliverable) {
      setIsCartScheduleMessageVisible(false);
      pending.onReady?.(false);
      return;
    }

    (async () => {
      const shouldProceed = await onValidateCartServiceabilityForLocationRef.current?.(
        pending.pincode,
        pending.location,
        {
          openClearCart: pending.openClearCart,
          isCurrentProductServiceable: true,
        },
      );
      pending.onReady?.(shouldProceed !== false);
    })();
  }, [
    isFromPDP,
    isFromCheckout,
    isDeliveryDatesLoading,
    deliveryDates,
    isDatesAvailable,
    selectedDomesticLocation?.pincode,
  ]);

  /**
   * Queues cart serviceability validation to run after delivery-dates confirms deliverability.
   * Committing location first triggers the delivery-dates saga via existing PDP effects.
   *
   * @param {Object} locationDetails
   * @param {Object} [options]
   * @param {boolean} [options.openClearCart]
   * @param {Function} [options.onReady]
   * @returns {void}
   */
  const queueCartValidationAfterDeliveryDates = (locationDetails, { openClearCart = false, onReady } = {}) => {
    setIsCartScheduleMessageVisible(false);
    // Country switch is handled by locationLock continue → ClearCart Change Country, not OOS APIs.
    if (!(cartCount > 0) || isFromCheckout || isCountryChanged) {
      onReady?.(true);
      return;
    }
    pendingCartValidationRef.current = {
      pincode: locationDetails?.pincode,
      location: locationDetails?.location,
      openClearCart,
      onReady,
      phase: "awaiting_start",
    };
  };

  /**
   * Continue Shopping Handler.
   */
  const onContinueClick = debounce(async (locationDetails) => {
    // On the home page, re-fetch the feed with the new pincode so widgets update immediately.
    // Mobile feed is handled by requestMobileStoreFeed in LocationLock.
    // Desktop feed is re-fetched here. The gift finder gate is included: confirming a
    // location there changes it for the whole session, so the page behind the drawer must
    // not be left showing a feed for the previous one.
    if (isHome && !isMobile) {
      const pincode = locationDetails?.pincode || "";
      const email = CookieUtil.getCookie("faul") || "";
      const fnpId = CookieUtil.getCookie("ab_user_id") || "";
      dispatch(requestDesktopFeed({ pincode, email, fnpId }));
    }
    const customDimension = { interaction_type: lastVisitedPincode && "automatically", page_type: window?.fnpPageType };
    dispatch(setPincodeErrorFromAddToCart(false));
    if (isEmpty(locationDetails) && ((isMandateForDesktop && !isMobile) || (isMandateForMobile && isMobile))) {
      dispatch(setMandateErrorAction(true));
      setIsMandatoryError(isMobile ? isMandateForMobile : isMandateForDesktop);
      const cleverTapData = {
        page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
        page_type: (typeof window !== "undefined" && window.fnpPageType) || "",
        validation_text: LOCATION_MANDATORY,
        validation_for: validationFor.LOCATION_FIELD,
        ...(isFromPDP && {
          area: cleverTapPDPCommonData?.area,
          pincode: cleverTapPDPCommonData?.pincode,
          pid: cleverTapPDPCommonData?.pid,
          sku_id: cleverTapPDPCommonData?.sku_id,
        }),
        flow: "new",
      };
      trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);

      setStatusText(LOCATION_MANDATORY);
    } else if (
      (!isMandateForMobile && isMobile) ||
      (!isMandateForDesktop && !isMobile) ||
      !isEmpty(selectedDomesticLocation) ||
      !isEmpty(locationDetails)
    ) {
      // Confirm: open ClearCart only here if cart items are unserviceable.
      // Selection already triggered the serviceability API; result is cached by pincode.
      // On PDP, skip cart APIs when the current product is not deliverable.
      if (locationDetails?.pincode) {
        const shouldProceed = await onValidateCartServiceabilityForLocation(
          locationDetails.pincode,
          locationDetails.location,
          {
            openClearCart: true,
            isCurrentProductServiceable: !(isFromPDP && isProductUnavailable),
          },
        );
        if (!shouldProceed) {
          return;
        }
      }

      /**
       * Applies the selected location to Redux, cookies, and parent continue handler.
       * @returns {Promise<void>}
       */
      const applyLocationUpdate = async () => {
        if (!isEmpty(locationDetails)) {
          clearSavedAddressSelection();
          if (!isFromCheckout) {
            dispatch(
              setSelectedDomesticLocation({
                pincode: locationDetails?.pincode,
                address: locationDetails?.location,
              }),
            );
          }

          customDimension.interaction_type = "manual";
          trackEvent(
            GA4_EVENTS.DELIVERY_LOCATION_ADDED,
            {
              eventCategory: "delivery_location_interaction",
              eventAction: "delivery_location_added",
              eventLabel: "continue_click",
              ...customDimension,
            },
            ["ga4"],
          );
        }

        if (onHandleContinueShopping !== undefined) {
          const shouldProceed = await onHandleContinueShopping(locationDetails);
          if (shouldProceed === false) {
            return;
          }
        }
      };

      const shouldDeferPdpLocationUpdate = isMobile && isFromPDP && !isFromCheckout && !isEmpty(locationDetails);

      if (shouldDeferPdpLocationUpdate) {
        dispatch(setIsShowMobileLocationLockAction(false));
        dispatch(setIsShowMobileLocationLockDrawerAction(false));
        setTimeout(() => {
          applyLocationUpdate();
        }, 320);
        return;
      }

      await applyLocationUpdate();

      if (!isFromCheckout && !(isFromPDP && isMobile)) {
        if (isMobile) {
          dispatch(setIsShowMobileLocationLockAction(false));
          dispatch(setIsShowMobileLocationLockDrawerAction(false));
        } else {
          dispatch(setIsShowDesktopLocationLockAction(false));
        }
        unlockBody();
      }
    }
  }, 100);

  /**
   * Confirm click.
   *
   * In capture mode this records the answer and nothing else — no cookies, no redux, no
   * navigation — because the caller applies the location later, at its own hand-off. The
   * pincode is optional there: confirming with an empty field answers the step with the
   * selected country alone, which the gate reads as "anywhere in this country".
   *
   * @returns {void}
   */
  const onConfirmClick = () => {
    const locationDetails = getLocationDetailsForContinue();

    if (locationCaptureMode) {
      onCaptureLocation?.(locationDetails);
      return;
    }

    onContinueClick(locationDetails);
  };

  /**
   * Handles the change in pincode or location input.
   * @param {object} event - The event object.
   */
  const onHandlePincodeLocationChange = (event) => {
    const { value } = event.target;
    if (value) {
      clearSavedAddressSelection();
    }
    if (isSuggestionSelected && value !== searchValue) {
      setSuggestionSelected(false);
    }
    setSearchValue(value);
    setIsMandatoryError(false);

    if (selectedCountry?.catelogID !== COUNTRIES.INDIA && value.length >= 2) {
      clearTimeout(timeoutId);
      const timeoutRef = setTimeout(() => {
        setSuggestions(getFilteredCitiesList(value, cityListData, "cityName").slice(0, 5));
      }, 100);
      setTimeoutId(timeoutRef);
      return;
    }
    const filterSearch = getPincodeSetter(domesticPincodeList, value);
    if (value.length >= 2 && value.match(digitsOnlyRegex)) {
      setDeliveryType("pincode");
      // Fetch pincode suggestions
      const currentPin = value.substr(0, 2);
      const firstElement = domesticPincodeList[0]?.pincode;
      if (!firstElement || firstElement.slice(0, 2) !== currentPin) {
        getPincodes(currentPin, dispatch, availablePincodes);
      } else if (value.length >= 6 && filterSearch.length === 0) {
        setDeliveryType("location");
        debouncedPredictionCall(value);
      }
      setSearchValue(value);
    } else if (value.length >= 3 && value.match(charactersAndDigitsOnlyRegex)) {
      setDeliveryType("location");
      // Fetch pincode suggestions using Google Maps API
      debouncedPredictionCall(value);
    } else {
      setDeliveryType("");
      setSuggestions([]);
    }
  };

  /**
   * Handles the selection of a pincode suggestion.
   * @param {object} suggestion - The selected suggestion.
   */
  const onHandlePincodeSelection = debounce(async (suggestion) => {
    removeHashtagFromURL(FROM_LOCATION_LOCK);
    clearSavedAddressSelection();
    setIsLocationNudgeVisible(false);
    setSuggestions([]);
    const { location, pincode: selectedPincode, lat, long } = suggestion;
    const locationDetails = { pincode: selectedPincode, location, lat, long };
    dispatch(clearMapFormLocation());

    // Keep lat/long in sync with the selected pincode; drop a stale value when the API has none.
    if (lat && long) {
      CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, { path: "/" });
      CookieUtil.setCookie(LAST_VISITED_LAT_LONG, `${lat},${long}`, {
        maxAge: 60 * 60 * 24 * 7,
        path: "/",
        encode: decodeURIComponent,
      });
    } else {
      CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, { path: "/" });
      CookieUtil.deleteCookie(LAST_VISITED_LAT_LONG, { path: "/" });
    }
    // Pincode selection has no Google place types (accuracy weightage level does not exist).
    CookieUtil.deleteCookie(LAST_VISITED_PLACE_TYPES, { path: "/" });
    CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_LEVEL, { path: "/" });

    setSearchValue(`${selectedPincode}, ${location}`);
    setSelectedLocationDetails(locationDetails);

    trackEvent(
      cleverTapCommonEvents.locationPincodeSelected,
      buildLocationSelectCleverTapData({
        pincode: selectedPincode,
        system_pincode: selectedPincode,
        area: location,
      }),
      ["clevertap"],
    );
    trackEvent(GA4_EVENTS?.PINCODE_ENTERED, { pincode: selectedPincode, pageType: window?.fnpPageType }, ["ga4"]);
    trackEvent(GA4_EVENTS?.PINCODE_AVAILABLE, { pincode: selectedPincode || lastVisitedPincode }, ["ga4"]);
    trackEvent(
      GA4_EVENTS.DELIVERY_LOCATION_ADDED,
      {
        eventCategory: "delivery_location_interaction",
        eventAction: "delivery_location_added",
        eventLabel: "pincode_selection",
      },
      ["ga4"],
    );

    // PDP: commit location first so delivery-dates runs before cart serviceability APIs.
    if (isFromPDP && !isFromCheckout) {
      if (isMobile) {
        commitLocationSelection(locationDetails);
        setSuggestionSelected(true);
        queueCartValidationAfterDeliveryDates(locationDetails);
      } else {
        setSuggestionSelected(true);
        dispatch(
          setSelectedDomesticLocation({
            pincode: selectedPincode,
            address: location,
          }),
        );
        queueCartValidationAfterDeliveryDates(locationDetails, {
          openClearCart: true,
          onReady: (shouldProceed) => {
            if (shouldProceed !== false) {
              onContinueClick(locationDetails);
            }
          },
        });
      }
    } else {
      // Capture mode: picking a suggestion only fills the field — Confirm is what answers
      // the step, so the user can still change country or clear the pincode first. It must
      // not run serviceability or open ClearCart either: nothing is committed yet.
      if (locationCaptureMode) {
        setSuggestionSelected(true);
        return;
      }

      // Non-PDP: keep immediate cart serviceability validation.
      await onValidateCartServiceabilityForLocation(selectedPincode, location);

      if (isFromPDP && !isMobile) {
        onContinueClick(locationDetails);
      } else {
        setSuggestionSelected(true);
        if (isFromPDP && isMobile) {
          commitLocationSelection(locationDetails);
        }
      }
    }
  }, 100);

  useEffect(() => {
    let customPincode = "";
    if (searchValue.length > 1 && searchValue.length <= 6) {
      customPincode = searchValue.trim();
      const newPinCodes = getPincodeSetter(domesticPincodeList, customPincode);

      // Auto-select when only one pincode suggestion is available and input is digits only
      // Works for partial pincodes (3-5 digits) and complete pincodes (6 digits)
      if (
        newPinCodes.length === 1 &&
        customPincode.length >= 3 &&
        customPincode.length <= 6 &&
        customPincode.match(digitsOnlyRegex) &&
        deliveryType === "pincode"
      ) {
        onHandlePincodeSelection(newPinCodes[0]);
        return;
      }

      setSuggestions(newPinCodes.slice(0, 5));
    }
    // onHandlePincodeSelection is a debounced handler recreated each render; intentionally
    // excluded so this effect only reacts to input/pincode-list/deliveryType changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue, domesticPincodeList, deliveryType]);

  /**
   * Handles the selection of a place suggestion from Google Places API.
   * @param {object} suggestion - The selected suggestion.
   */
  const onHandleSelectedPlace = debounce(async (suggestion) => {
    if (suggestion) {
      const matchedSuggestionIndex = (suggestions || []).findIndex(
        (item) => item === suggestion || (suggestion?.place_id && item?.place_id === suggestion.place_id),
      );
      const suggestionPosition = matchedSuggestionIndex >= 0 ? matchedSuggestionIndex + 1 : undefined;
      clearSavedAddressSelection();
      setSuggestions([]);

      const options = { maxAge: 60 * 60 * 24 * 7, path: "/", encode: decodeURIComponent };
      dispatch(clearMapFormLocation());
      CookieUtil.setCookie(LAST_VISITED_PLACE_ID, suggestion?.place_id, options);
      // Accuracy type for the add-to-cart payload = last Google place type of the selection (e.g. "political").
      const accuracyType = Array.isArray(suggestion?.types) ? suggestion.types[suggestion.types.length - 1] : "";
      if (accuracyType) {
        CookieUtil.setCookie(LAST_VISITED_ACCURACY_TYPE, String(accuracyType), options);
      } else {
        CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_TYPE, { path: "/" });
      }
      // All Google types of the selection, matched against the accuracy weightage (add address prefill).
      const placeTypes = Array.isArray(suggestion?.types) ? suggestion.types.filter(Boolean).join(",") : "";
      if (placeTypes) {
        CookieUtil.setCookie(LAST_VISITED_PLACE_TYPES, placeTypes, options);
      } else {
        CookieUtil.deleteCookie(LAST_VISITED_PLACE_TYPES, { path: "/" });
      }
      // Accuracy level of the pick, kept in a cookie so serviceability APIs still get it after a refresh.
      if (placeTypes) await execDataLayer(fetchAccuracyWeightage, endpointConst.accuracyWeightage);
      const accuracyLevel = placeTypes ? getAccuracyLevelForTypes(suggestion.types) : "";
      // "Enter the exact area" nudge: only for a low accuracy place (never for a pincode).
      setIsLocationNudgeVisible(isLowAccuracyPlaceTypes(suggestion?.types));
      if (accuracyLevel) {
        CookieUtil.setCookie(LAST_VISITED_ACCURACY_LEVEL, accuracyLevel, options);
      } else {
        CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_LEVEL, { path: "/" });
      }
      {
        const placeDetails = await getPlaceDetailsByPlaceId(suggestion?.place_id);
        let pincodeSelected = "";
        // Lat/long of the picked place (like a pincode selection): the place details API, else Google's place
        // location; a stale value from an earlier location is dropped when neither has one.
        const fallbackLatLong =
          Number(placeDetails?.lat) && Number(placeDetails?.long)
            ? null
            : await getGooglePlaceLatLong(suggestion?.place_id);
        const latitude = Number(placeDetails?.lat) || Number(fallbackLatLong?.lat) || 0;
        const longitude = Number(placeDetails?.long) || Number(fallbackLatLong?.long) || 0;
        if (latitude && longitude) {
          CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, { path: "/" });
          CookieUtil.setCookie(LAST_VISITED_LAT_LONG, `${latitude},${longitude}`, options);
        } else {
          CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, { path: "/" });
          CookieUtil.deleteCookie(LAST_VISITED_LAT_LONG, { path: "/" });
        }
        pincodeSelected = placeDetails?.pincode ? String(placeDetails.pincode) : "";

        // PDP: commit location first so delivery-dates runs before cart serviceability APIs.
        const locationDetails = { pincode: pincodeSelected, location: suggestion?.description };
        setSearchValue(suggestion?.description || pincodeSelected);
        setSelectedLocationDetails(locationDetails);
        removeHashtagFromURL(FROM_LOCATION_LOCK);

        trackEvent(
          cleverTapCommonEvents.locationAreaSelected,
          buildLocationSelectCleverTapData({
            area: suggestion?.description,
            system_pincode: pincodeSelected,
            pincode: pincodeSelected,
            location_accuracy: accuracyLevel || "",
            accuracy_type: accuracyType || "",
            suggestion_position: suggestionPosition,
          }),
          ["clevertap"],
        );
        trackEvent(GA4_EVENTS?.PINCODE_ENTERED, { pincode: pincodeSelected, pageType: window?.fnpPageType }, ["ga4"]);
        trackEvent(GA4_EVENTS?.PINCODE_AVAILABLE, { pincode: pincodeSelected }, ["ga4"]);
        trackEvent(
          GA4_EVENTS.DELIVERY_LOCATION_ADDED,
          {
            eventCategory: "delivery_location_interaction",
            eventAction: "delivery_location_added",
            eventLabel: "pincode_selection",
          },
          ["ga4"],
        );

        if (isFromPDP && !isFromCheckout) {
          if (isMobile) {
            commitLocationSelection(locationDetails);
            setSuggestionSelected(true);
            queueCartValidationAfterDeliveryDates(locationDetails);
          } else {
            setSuggestionSelected(true);
            dispatch(
              setSelectedDomesticLocation({
                pincode: pincodeSelected,
                address: suggestion?.description,
              }),
            );
            queueCartValidationAfterDeliveryDates(locationDetails, {
              openClearCart: true,
              onReady: (shouldProceed) => {
                if (shouldProceed !== false) {
                  onContinueClick(locationDetails);
                }
              },
            });
          }
        } else {
          // Capturing: selection only fills the field — see onHandlePincodeSelection.
          if (locationCaptureMode) {
            setSuggestionSelected(true);
            return;
          }

          await onValidateCartServiceabilityForLocation(pincodeSelected, suggestion?.description);

          if (isFromPDP && !isFromCheckout && !isMobile) {
            onContinueClick(locationDetails);
          } else {
            setSuggestionSelected(true);
            if (isFromPDP && !isFromCheckout && isMobile) {
              commitLocationSelection(locationDetails);
            }
          }
        }
      }
    }
  }, 100);

  /**
   * Clears the suggestion field and resets the input value.
   */
  const onHandleClearSuggestionField = (e) => {
    e.stopPropagation();
    if (eventTriggered) return;
    setEventTriggered(true);
    clearSavedAddressSelection();
    setSearchValue("");
    setSelectedLocationDetails({});
    setIsCartScheduleMessageVisible(false);
    pendingCartValidationRef.current = null;
    setSuggestionSelected(false);
    setSelectedRow(0);
    setIsLocationNudgeVisible(false);

    // Clearing the field is only an edit while capturing: the stored location must survive
    // until Find Gifts, and an abandoned quiz must leave it untouched. Everywhere else the
    // X is a real "remove my location", so it still tears the delivery state down.
    if (!locationCaptureMode) {
      dispatch(setIsSavedAddressUserName(null));
      dispatch(setDeliveryDetailsAction({}));
      dispatch(resetMultiProductDateRange());
      dispatch(successToFetchBlockedDatesAction({}));
      dispatch(requestResetShippingDateAndSlot());
      dispatch(resetPdpAddOnState());
      dispatch(setSelectedDomesticLocation({}));

      // Resetting the Delivery Date Error after clearing the location/pincode
      if (deliveryDateError) {
        dispatch(setDeliveryDateErrorFromAddToCart(false));
      }

      const pathOptions = { path: "/" };
      CookieUtil.deleteCookie(SELECTED_SAVED_ADDRESS, pathOptions);
      CookieUtil.deleteCookie(SELECTED_SAVED_ADDRESS_USERNAME, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_PINCODE, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_ADDRESS, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_ADDRESS_LAT_LONG, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_LAT_LONG, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_PLACE_TYPES, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_LEVEL, pathOptions);
      CookieUtil.deleteCookie(LAST_VISITED_ACCURACY_TYPE, pathOptions);
    }

    const pageLocationRemovedData = buildLocationSelectCleverTapData({ removed_location: searchValue });
    trackEvent(cleverTapCommonEvents.locationRemoved, pageLocationRemovedData, ["clevertap"]);

    if (isFromGiftFinderGate) {
      requestAnimationFrame(() => {
        document.getElementById("pincode-location-input")?.focus();
      });
    }

    setTimeout(() => {
      setEventTriggered(false);
    }, 5000);
  };

  /**
   * This function is used for keyboard Up/Down and Enter keys for navigating through Location/Pincode field.
   * @param {event} event object for getting keyboard keys.
   * @returns {void}
   */
  const onHandleKeyDown = (event) => {
    if (event.key === "ArrowUp") {
      if (selectedRow <= 0) {
        setSelectedRow(suggestions?.length - 1);
      } else {
        setSelectedRow((prevPin) => prevPin - 1);
      }
    } else if (event.key === "ArrowDown") {
      if (selectedRow >= suggestions?.length - 1) {
        setSelectedRow(0);
      } else {
        setSelectedRow((prevPin) => prevPin + 1);
      }
    } else if (event.key === "Enter" && selectedRow >= 0) {
      if (deliveryType === "pincode") {
        onHandlePincodeSelection(suggestions[selectedRow]);
        setSuggestions([]);
        setSuggestionSelected(true);
      } else if (deliveryType === "location") {
        onHandleSelectedPlace(suggestions[selectedRow]);
      }
      setSelectedRow(0);
    } else if (event.key === "Backspace") {
      if (suggestions?.length === 0) {
        setSelectedRow(0);
      }
    }
  };

  /** onHandleFocus handles*/
  const onHandleFocus = () => {
    setIsMandatoryError(false);
    !isMobile && setMandatoryError(false);
    dispatch(setIsHideSavedAddresses(true));
    dispatch(onFocusRequestFromTextfield(true));
    onFocusChangeHandler?.(true);
    if (typeof document !== "undefined" && isMobile && checkLoginStatus) {
      setTimeout(() => {
        const element = document.getElementById("pincode-location-input");
        const drawerContainer = document.querySelector("[id^='mobileLocationLock']");
        if (element && drawerContainer) {
          const containerRect = drawerContainer.getBoundingClientRect();
          const scrollPosition = element.offsetTop - containerRect.top + drawerContainer.scrollTop;

          drawerContainer.scrollTop = Math.max(0, scrollPosition - 50);
        } else if (element) {
          element.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }

    dispatch(setPincodeErrorFromAddToCart(false));
    if (isFromPDP && !isMobile) {
      trackEvent(
        cleverTapCommonEvents.locationClicked,
        {
          ...ishomePlpPdpLocationData,
          is_location_available: !!(lastVisitedPincode || lastVisitedAddress),
          flow: "new",
        },
        ["clevertap"],
      );
    }
  };

  /**
   * Handles the selection of an international location.
   * @param {Object} e - The selected location object containing city information.
   */
  const handleInternationalSelection = (e) => {
    const locationDetails = { pincode: e?.cityId, location: e?.cityName };
    onHandleContinueShopping(locationDetails);
  };

  // Capture mode keeps its Confirm button: the gate accepts a country on its own, so the
  // pincode is optional and there has to be something to press when the field is empty.
  const shouldShowContinueButton = !isJoinedFieldLayout && !isContinueButtonHidden;
  const hasLocationInput = Boolean(searchValue?.trim());
  const showProductUnavailableUi = isProductUnavailable && hasLocationInput;
  const showCartScheduleMessage =
    isCartScheduleMessageVisible &&
    !isCountryChanged &&
    hasLocationInput &&
    !isContinueButtonHidden &&
    !isError &&
    !isMandatoryError &&
    !showProductUnavailableUi;
  const isContinueButtonDisabled =
    !isCountryChanged &&
    (Boolean(serviceabilityErrorMessage) ||
      showProductUnavailableUi ||
      // The gate accepts a country on its own — pincode is optional there, so an empty
      // selection must not block Confirm. Real errors above still do.
      (!isFromGiftFinderGate && !isSuggestionSelected && isEmpty(selectedLocationDetails)));

  /**
   * Closes the drawer and redirects via BuyNow Explore CTA handler.
   * @returns {void}
   */
  const onExploreAvailableProductsClick = () => {
    onExploreAvailableProducts?.();
  };

  return (
    <>
      <TextField
        placeholder={INTEGRATED_PINCODE_GOOGLE_SUGGESTION}
        type="text"
        id="pincode-location-input"
        isRequired={false}
        variant="primary"
        inputValue={searchValue}
        onChange={!isDisable ? onHandlePincodeLocationChange : () => null}
        onKeyDown={onHandleKeyDown}
        onFocus={onHandleFocus}
        onBlur={() => dispatch(onFocusRequestFromTextfield(false))}
        isError={isMandatoryError || isError || showProductUnavailableUi}
        message={isMandatoryError || isError || hasLocationInput ? messageText || statusText : ""}
        messageClass={messageClass}
        endAdornment={
          isSuggestionSelected && (
            <XCircleIcon
              size={22}
              weight="fill"
              color="#444444"
              className="cursor-pointer opacity-40"
              onClick={onHandleClearSuggestionField}
            />
          )
        }
        autoFocus={isAutoFocus}
        readOnly={isSuggestionSelected}
        classes={`overflow-hidden text-ellipsis whitespace-nowrap ${isJoinedFieldLayout ? "rounded-s-none" : ""} font-medium`}
        onClick={isDisable ? onPincodeLocationClickHandler : () => null}
        isFromPDP={isFromPDP}
        isErrorMsgAnimate={isErrorMsgAnimate}
        isFromCheckout={isCheckout}
      />
      {showProductUnavailableUi && isFromPDP && !isFromCheckout && onExploreAvailableProducts ? (
        <div className="mt-12">
          <ButtonComponent
            size="medium"
            buttonVariant="outlined"
            label={constants.SHOW_SIMILAR}
            id="explore-available-products-button"
            onClick={onExploreAvailableProductsClick}
            classes="w-full"
            btnParentClasses="w-full"
            aria-label={constants.SHOW_SIMILAR}
            backGroundColor="[&&]:border-greySoft"
          />
        </div>
      ) : null}
      {showCartScheduleMessage ? (
        <div className={`mt-8 flex items-center gap-4 ${isFromPDP ? messageClass : ""}`}>
          <InfoIcon size={16} weight="fill" className="text-blue-100" />
          <Typography Tag="p" classes="text-12 text-blue-100 font-500 leading-20">
            {DELIVERY_SHOULD_BE_SCHEDULED_MESSAGE}
          </Typography>
        </div>
      ) : isLocationNudgeVisible && !isError & !isMandatoryError ? (
        <div
          className={`mt-8 flex items-center gap-4 rounded-4 bg-blue-100 bg-opacity-[0.06] p-4 ${isFromPDP ? messageClass : ""}`}
        >
          <CustomImage
            src="/icons/info-circle-blue.svg"
            width={16}
            height={16}
            alt="delivery info"
            isPriority={false}
            sizes="10vw"
          />
          <Typography Tag="p" classes="text-12 text-grey-500 font-500 leading-20">
            {NUDGE_TEXT_FOR_AREA_SECTOR}
          </Typography>
        </div>
      ) : (
        ""
      )}
      <div
        className={`${isJoinedFieldLayout && suggestions?.length > 0 ? "absolute left-0 top-56 z-1 w-full rounded-8 border-1 border-greySoft bg-white-900 p-8" : ""}`}
      >
        {suggestions?.length > 0 ? (
          <ListDropDownSection
            suggestions={suggestions}
            searchValue={searchValue}
            deliveryType={deliveryType}
            onHandlePincodeSelection={onHandlePincodeSelection}
            selectedRow={selectedRow}
            onHandleSelectedPlace={onHandleSelectedPlace}
            selectedCountry={selectedCountry}
            handleInternationalSelection={handleInternationalSelection}
          />
        ) : (
          <></>
        )}
      </div>
      {shouldShowContinueButton ? (
        // The gate renders inline on both breakpoints. The mobile branch below portals the
        // button into `mobileLocationLock` / `mobileLocationLockPDP` / `mobileLocationLockDA`,
        // none of which exist inside the quiz card — the portal found no target and the
        // button silently disappeared.
        (!isMobile && !isFromCheckout) || isFromGiftFinderGate ? (
          <div className="pt-16">
            <ButtonComponent
              size="medium"
              buttonVariant="filled"
              label={CONFIRM}
              id="location-lock-continue-button"
              onClick={onConfirmClick}
              btnParentClasses={formatClassNames(isContinueButtonDisabled && "opacity-50")}
              backGroundColor={isContinueButtonDisabled ? "bg-grey-200" : "bg-fnp-300"}
              isDisabled={isContinueButtonDisabled}
            />
          </div>
        ) : (
          <>
            {/* The gift finder gate lets the user pick country and pincode together as
                one answer, so a country change is expected rather than something to warn
                about — and the banner would push the fields down mid-question. */}
            {isCountryChanged && !isFromGiftFinderGate && (
              <CountryChangeInfoMobile
                countryName={selectedCountry?.countryName}
                isFromOutsideDrawer={isFromOutsideDrawer}
              />
            )}

            <Portal
              rootId={
                isVisibleLocationMobileDrawer
                  ? "locationLockWrapper"
                  : !loggedin && isFromPDP && !isFromCheckout
                    ? "mobileLocationLockPDP"
                    : isFromCheckout && isMobile
                      ? "mobileLocationLockDA"
                      : "mobileLocationLock"
              }
              renderOnDidMount={true}
            >
              {/** this section visible on domestic */}
              {!isVisibleLocationMobileDrawer && !suggestions?.length > 0 && !loggedin && !isFromGiftFinderGate && (
                <BlankSpaceUI
                  classes={!isFromCheckout && isMobile ? "absolute bottom-[84px]" : "absolute bottom-[136px]"}
                />
              )}
              <div
                className={formatClassNames(
                  "w-full",
                  // In the gift finder gate this sits inside the question card, so the
                  // white panel and its divider would read as a second sheet on top of it.
                  !isFromGiftFinderGate && "bg-white-900",
                  isMobile && isSeparateFieldsDesign && !isFromOutsideDrawer && "relative p-16",
                  isMobile && isFromOutsideDrawer && "relative pt-16",
                  isMobile && !isSeparateFieldsDesign && !isVisibleLocationMobileDrawer && "absolute bottom-0 p-16",
                  !loggedin && isFromPDP && "mt-auto",
                  isFromCheckout && "mt-auto",
                  isMobile && !isFromCheckout && !isFromGiftFinderGate && "border-t-1 border-thinLightGrey",
                )}
              >
                <ButtonComponent
                  id="location-lock-continue-button"
                  size="medium"
                  buttonVariant="filled"
                  label={CONFIRM}
                  onClick={onConfirmClick}
                  btnParentClasses={formatClassNames(
                    isFromCheckout && !isMobile ? "border-t-1 border-thinLightGrey p-16" : "",
                  )}
                  backGroundColor={isContinueButtonDisabled ? "bg-grey-200" : "bg-fnp-300"}
                  isDisabled={isContinueButtonDisabled}
                />
              </div>
            </Portal>
          </>
        )
      ) : (
        <></>
      )}
    </>
  );
};

export default React.memo(PincodeLocationSuggestion);

PincodeLocationSuggestion.propTypes = {
  onHandleContinueShopping: PropTypes.func.isRequired,
  isFromPDP: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
  isError: PropTypes.bool,
  messageText: PropTypes.string,
  messageClass: PropTypes.string,
  isSuggestionSelected: PropTypes.bool,
  setSuggestionSelected: PropTypes.func.isRequired,
  searchValue: PropTypes.string.isRequired,
  setSearchValue: PropTypes.func.isRequired,
  selectedCountry: PropTypes.object.isRequired,
  isDisable: PropTypes.bool,
  onPincodeLocationClickHandler: PropTypes.func,
  urlIdentifier: PropTypes.string,
  cleverTapPDPCommonData: PropTypes.object,
  setMandatoryError: PropTypes.func,
  isLocationNudgeVisible: PropTypes.bool,
  setIsLocationNudgeVisible: PropTypes.func,
  isSeparateFieldsDesign: PropTypes.bool,
  isContinueButtonHidden: PropTypes.bool,
  onClearSavedAddressSelection: PropTypes.func,
  serviceabilityErrorMessage: PropTypes.string,
  isCountryChanged: PropTypes.bool,
  isProductUnavailable: PropTypes.bool,
  onExploreAvailableProducts: PropTypes.func,
  onSuggestionsVisibilityChange: PropTypes.func,
  onFocusChangeHandler: PropTypes.func,
  isFromOutsideDrawer: PropTypes.bool,
  isFromGiftFinderGate: PropTypes.bool,
  locationCaptureMode: PropTypes.shape({ onCapture: PropTypes.func.isRequired }),
  onCaptureLocation: PropTypes.func,
};
